import '../../styles/PhotoUpload.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { useParams } from 'react-router-dom';

import {
  createImagePreview,
  uploadImage,
} from '../../utils/imageUtils';

interface PhotoUploadPropsWithThumbnail {
  imageUrl?: string;
  imageThumbnailUrl?: string;
  onImageUpload: (urls: { imageUrl: string; imageThumbnailUrl: string }) => void;
  onUploadingChange?: (uploading: boolean) => void;
  className?: string;
  label?: string;
  withThumbnail?: true;
}

interface PhotoUploadPropsWithoutThumbnail {
  imageUrl?: string;
  onImageUpload: (url: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  className?: string;
  label?: string;
  withThumbnail: false;
}

type PhotoUploadProps = PhotoUploadPropsWithThumbnail | PhotoUploadPropsWithoutThumbnail;

/**
 * A reusable component for uploading and previewing photos
 */
export default function PhotoUpload(props: PhotoUploadProps) {
  const {
    imageUrl,
    className = 'skill-photo-upload',
    label,
    withThumbnail = true,
  } = props;
  const imageThumbnailUrl = (props as PhotoUploadPropsWithThumbnail).imageThumbnailUrl;
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(imageUrl || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { sessionId } = useParams<{ sessionId: string }>();

  // Update imagePreview when imageUrl prop changes (e.g., when switching athletes)
  useEffect(() => {
    setImagePreview(imageUrl || null);
  }, [imageUrl]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);
      
      try {
        // Create a preview URL
        const previewUrl = await createImagePreview(file);
        setImagePreview(previewUrl);
        
        // Automatically upload the image when selected
        await handleImageUpload(file);
      } catch (error) {
        console.error('Error handling image:', error);
        setError('Failed to process image');
      }
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!sessionId || !file) return;
    try {
      setUploading(true);
      setError('');
      props.onUploadingChange?.(true);
      if (withThumbnail) {
        // Upload image and thumbnail to Firebase Storage
        const { imageUrl, imageThumbnailUrl } = await uploadImage(file, sessionId);
        (props as PhotoUploadPropsWithThumbnail).onImageUpload({ imageUrl, imageThumbnailUrl });
      } else {
        // Only upload the main image
        const { imageUrl } = await uploadImage(file, sessionId);
        (props as PhotoUploadPropsWithoutThumbnail).onImageUpload(imageUrl);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
      setImagePreview(null);
    } finally {
      setUploading(false);
      props.onUploadingChange?.(false);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    if (withThumbnail) {
      (props as PhotoUploadPropsWithThumbnail).onImageUpload({ imageUrl: '', imageThumbnailUrl: '' });
    } else {
      (props as PhotoUploadPropsWithoutThumbnail).onImageUpload('');
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      {label && <label className="photo-upload-label">{label}</label>}
      
      {!imagePreview ? (
        <div className="photo-upload-container">
          <button 
            type="button"
            className="photo-upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <span className="camera-icon">📷</span>
            {uploading ? 'Uploading...' : 'Take Photo'}
          </button>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageChange}
            ref={fileInputRef}
            className="hidden-file-input"
          />
        </div>
      ) : (
        <div className="image-preview">
          <img src={imagePreview} alt="Preview" />
          <button 
            type="button" 
            className="remove-photo-button"
            onClick={handleRemoveImage}
          >
            Remove
          </button>
        </div>
      )}
      {error && <div className="upload-error">{error}</div>}
    </div>
  );
} 